/**
 * The engine hook.
 *
 * Holds the form state, recomputes visibility and validation, and paginates by section. Everything
 * it does is a call into the pure modules — the hook is the React adapter, not the logic — which is
 * why the tests can cover the behaviour that matters without a DOM.
 */

import { useCallback, useMemo, useReducer, useState } from 'react';
import { computeVisibility, visibleSections } from './logic.ts';
import { clearValue, confirmAllPrefills, confirmPrefill, createState, setValue } from './state.ts';
import { validate } from './validate.ts';
import { buildSubmission } from './payload.ts';
import type { CreateStateOptions, PrefillBundle } from './state.ts';
import type { Problem } from './validate.ts';
import type { SubmissionPayload } from './payload.ts';
import type { Answer, ContextValue, Field, FormSchema, FormState, PhotoRef, Section } from './types.ts';

type Action =
  | { readonly kind: 'set'; readonly fieldId: string; readonly value: Answer; readonly now: number }
  | { readonly kind: 'clear'; readonly fieldId: string; readonly now: number }
  | { readonly kind: 'confirm'; readonly fieldId: string; readonly now: number }
  | { readonly kind: 'confirmAll'; readonly now: number };

function reduce(state: FormState, action: Action): FormState {
  switch (action.kind) {
    case 'set':
      return setValue(state, action.fieldId, action.value, action.now);
    case 'clear':
      return clearValue(state, action.fieldId, action.now);
    case 'confirm':
      return confirmPrefill(state, action.fieldId, action.now);
    case 'confirmAll':
      return confirmAllPrefills(state, action.now);
  }
}

export interface UseFormEngineOptions {
  readonly schema: FormSchema;
  readonly prefill?: PrefillBundle;
  readonly context?: Readonly<Record<string, ContextValue>>;
  /** Injectable clock, so tests are deterministic and the payload timestamps are checkable. */
  readonly now?: () => number;
}

export interface Step {
  readonly section: Section;
  readonly fields: readonly Field[];
}

export interface FormEngine {
  readonly state: FormState;
  /** Only the sections and fields currently on screen, in document order. */
  readonly steps: readonly Step[];
  readonly stepIndex: number;
  readonly step: Step | undefined;
  /** True once the technician has walked past the last step. */
  readonly onReview: boolean;
  readonly problems: readonly Problem[];
  readonly problemsHere: readonly Problem[];
  readonly canAdvance: boolean;
  readonly canSubmit: boolean;
  readonly set: (fieldId: string, value: Answer) => void;
  readonly clear: (fieldId: string) => void;
  readonly confirm: (fieldId: string) => void;
  readonly confirmAll: () => void;
  readonly addPhoto: (fieldId: string, photo: PhotoRef) => void;
  readonly removePhoto: (fieldId: string, blobId: string) => void;
  readonly goTo: (sectionId: string) => void;
  readonly next: () => void;
  readonly back: () => void;
  readonly build: () => SubmissionPayload;
}

export function useFormEngine(options: UseFormEngineOptions): FormEngine {
  const { schema, prefill, context, now = Date.now } = options;

  const [state, dispatch] = useReducer(
    reduce,
    undefined,
    (): FormState => {
      const init: CreateStateOptions = {
        schema,
        now: now(),
        ...(context !== undefined ? { context } : {}),
        ...(prefill !== undefined ? { prefill } : {}),
      };
      return createState(init);
    },
  );

  // Step index is held as a section id rather than a number. Display logic can remove the section
  // the technician is standing on — answer No and three sections vanish — and a numeric index would
  // then silently point at a different question than the one they were looking at.
  const [cursor, setCursor] = useState<string | 'review'>(() => 'start');

  const visibility = useMemo(() => computeVisibility(schema, state), [schema, state]);
  const steps = useMemo(() => visibleSections(schema, state, visibility), [schema, state, visibility]);
  const problems = useMemo(() => validate(schema, state, { visibility }), [schema, state, visibility]);

  const stepIndex = useMemo(() => {
    if (cursor === 'review') return steps.length;
    if (cursor === 'start') return 0;
    const found = steps.findIndex((s) => s.section.id === cursor);
    // The section vanished. Land on the next one that still exists rather than falling off the end.
    return found >= 0 ? found : 0;
  }, [cursor, steps]);

  const onReview = cursor === 'review' || (steps.length > 0 && stepIndex >= steps.length);
  const step = onReview ? undefined : steps[stepIndex];

  const problemsHere = useMemo(
    () => (step ? problems.filter((p) => p.sectionId === step.section.id) : []),
    [problems, step],
  );

  const set = useCallback((fieldId: string, value: Answer) => dispatch({ kind: 'set', fieldId, value, now: now() }), [now]);
  const clear = useCallback((fieldId: string) => dispatch({ kind: 'clear', fieldId, now: now() }), [now]);
  const confirm = useCallback((fieldId: string) => dispatch({ kind: 'confirm', fieldId, now: now() }), [now]);
  const confirmAll = useCallback(() => dispatch({ kind: 'confirmAll', now: now() }), [now]);

  const addPhoto = useCallback(
    (fieldId: string, photo: PhotoRef) => {
      const current = state.entries[fieldId]?.value;
      const existing = Array.isArray(current) ? (current as readonly PhotoRef[]) : [];
      dispatch({ kind: 'set', fieldId, value: [...existing, photo], now: now() });
    },
    [state.entries, now],
  );

  const removePhoto = useCallback(
    (fieldId: string, blobId: string) => {
      const current = state.entries[fieldId]?.value;
      const existing = Array.isArray(current) ? (current as readonly PhotoRef[]) : [];
      dispatch({ kind: 'set', fieldId, value: existing.filter((p) => p.blobId !== blobId), now: now() });
    },
    [state.entries, now],
  );

  const next = useCallback(() => {
    const following = steps[stepIndex + 1];
    setCursor(following ? following.section.id : 'review');
  }, [steps, stepIndex]);

  const back = useCallback(() => {
    if (onReview) {
      const last = steps[steps.length - 1];
      setCursor(last ? last.section.id : 'start');
      return;
    }
    const previous = steps[stepIndex - 1];
    if (previous) setCursor(previous.section.id);
  }, [onReview, steps, stepIndex]);

  const goTo = useCallback((sectionId: string) => setCursor(sectionId), []);

  const build = useCallback(
    () => buildSubmission(schema, state, { completedAt: now(), visibility }),
    [schema, state, now, visibility],
  );

  return {
    state,
    steps,
    stepIndex,
    step,
    onReview,
    problems,
    problemsHere,
    canAdvance: problemsHere.length === 0,
    canSubmit: problems.length === 0,
    set,
    clear,
    confirm,
    confirmAll,
    addPhoto,
    removePhoto,
    goTo,
    next,
    back,
    build,
  };
}
